// Brand art in the DOM (art-assets §3): the play-card wordmark and the favicon, each with its
// procedural placeholder for when `assets/brand/*.png` is absent.
// - Wordmark placeholder: "ball.io" in Fredoka 700, each letter in the next palette colour with
//   a darker same-hue outline; the "." is a small cell (solid fill, darker rim).
// - Favicon placeholder: a palette-coloured ball with a two-dot face, drawn to a canvas and set
//   as a data URL on `<link rel="icon">`.
//
// Consumers: ui/menu/home.ts (wordmark), app.ts (favicon at boot).

import { PLAYER_PALETTE, type Rgb, rgbToCss, scaleRgb, SPECIAL_COLORS } from '@ballio/shared';
import type { AssetImage } from '../assets/loader.js';
import { h } from './dom.js';

export const BRAND_NAME = 'ball.io';

/** Outline darkening for the wordmark letters (deeper than the cell rim, for legibility). */
const LETTER_RIM_FACTOR = 0.62;

export interface WordmarkGlyph {
  /** The character; "." is drawn as a small ball. */
  readonly ch: string;
  readonly fill: Rgb;
  readonly rim: Rgb;
  readonly dot: boolean;
}

/** The placeholder wordmark's glyphs: each in the next palette colour (art-assets §3). */
export function wordmarkGlyphs(
  text: string = BRAND_NAME,
  palette: readonly Rgb[] = PLAYER_PALETTE,
): WordmarkGlyph[] {
  return Array.from(text).map((ch, i) => {
    const fill = palette.length > 0 ? (palette[i % palette.length] as Rgb) : SPECIAL_COLORS.plain;
    return { ch, fill, rim: scaleRgb(fill, LETTER_RIM_FACTOR), dot: ch === '.' };
  });
}

/** The brand colour: the wordmark dot's colour (also the favicon ball). */
export function brandColor(): Rgb {
  const dot = wordmarkGlyphs().find((g) => g.dot);
  return dot ? dot.fill : SPECIAL_COLORS.virus;
}

function imageSize(img: AssetImage): { w: number; h: number } {
  if ('naturalWidth' in img) return { w: img.naturalWidth, h: img.naturalHeight };
  return { w: img.width, h: img.height };
}

export interface Wordmark {
  readonly el: HTMLElement;
  /** The owner's `brand/wordmark.png` once loaded; null keeps (or restores) the placeholder. */
  setImage(img: AssetImage | null): void;
}

export function createWordmark(): Wordmark {
  const glyphs = h(
    'span',
    { class: 'wm-glyphs', 'aria-hidden': 'true' },
    wordmarkGlyphs().map((g) =>
      g.dot
        ? h('span', {
            class: 'wm-dot',
            style: { '--wm-fill': rgbToCss(g.fill), '--wm-rim': rgbToCss(g.rim) },
          })
        : h(
            'span',
            {
              class: 'wm-letter',
              style: { '--wm-fill': rgbToCss(g.fill), '--wm-rim': rgbToCss(g.rim) },
            },
            g.ch,
          ),
    ),
  );
  const el = h('h1', { class: 'wordmark', 'aria-label': BRAND_NAME }, glyphs);
  let current: AssetImage | null = null;

  return {
    el,
    setImage(img: AssetImage | null): void {
      if (img === current) return;
      current = img;
      if (!img) {
        el.replaceChildren(glyphs);
        el.classList.remove('has-art');
        return;
      }
      const { w, h: ht } = imageSize(img);
      if (!(w > 0 && ht > 0)) return;
      const canvas = h('canvas', { class: 'wm-art', width: w, height: ht, 'aria-hidden': 'true' });
      canvas.getContext('2d')?.drawImage(img, 0, 0, w, ht);
      el.replaceChildren(canvas);
      el.classList.add('has-art');
    },
  };
}

/** Draws the favicon (the owner's art scaled, or the placeholder ball) and returns a PNG data URL. */
export function faviconDataUrl(doc: Document, art: AssetImage | null, size = 64): string | null {
  const canvas = doc.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const g = canvas.getContext('2d');
  if (!g) return null;
  if (art) {
    g.drawImage(art, 0, 0, size, size);
  } else {
    const c = brandColor();
    const r = size * 0.44;
    const cx = size / 2;
    const cy = size / 2;
    g.fillStyle = rgbToCss(c);
    g.strokeStyle = rgbToCss(scaleRgb(c, 0.8));
    g.lineWidth = size * 0.07;
    g.beginPath();
    g.arc(cx, cy, r, 0, Math.PI * 2);
    g.fill();
    g.stroke();
    g.fillStyle = rgbToCss(SPECIAL_COLORS.ink);
    for (const dx of [-0.16, 0.16]) {
      g.beginPath();
      g.ellipse(cx + dx * size, cy - size * 0.06, size * 0.055, size * 0.085, 0, 0, Math.PI * 2);
      g.fill();
    }
  }
  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

/** Points `<link rel="icon">` at the favicon (creating the link if needed). */
export function applyFavicon(doc: Document, art: AssetImage | null): void {
  const url = faviconDataUrl(doc, art);
  if (!url) return;
  let link = doc.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = doc.createElement('link');
    link.rel = 'icon';
    doc.head.appendChild(link);
  }
  link.type = 'image/png';
  link.href = url;
}
